import { adminHostPoolQueryAtom, type AdminHostPoolWithType } from "@/admin/host_pools/hostPoolsQuery.ts";
import { LeftRight } from "@/components/LeftRight.tsx";
import { WSelect, type WSelectStyleProps, type WSelectValueProps } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { softInvariant } from "@/modules/error/errorStream.ts";
import { plural } from "@/utils/plural.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";

export interface PoolSelectProps extends WSelectStyleProps, WSelectValueProps<AdminHostPoolWithType, string> {
    items: AdminHostPoolWithType[];
    /** Only pools with the same storage pool as this pool will be shown */
    basePoolUuid: string;
    location: string;
}

function PoolSelectCustom({ items, ...props }: PoolSelectProps) {
    return (
        <WSelect
            aria-label="Select pool"
            items={items}
            getKey={(item) => item.uuid}
            getTextValue={(item) => item.name}
            searchItems
            renderSelectedValue={(item) => (
                <LeftRight
                    icon={<MaskIcon className="jp-hostpool-icon color-primary size-1rem" />}
                    title={item.name}
                    right={plural(item.hosts_assigned_count, "host", "hosts")}
                />
            )}
            {...props}
        >
            {(item) => (
                <LeftRight
                    icon={<MaskIcon className="jp-hostpool-icon color-primary size-1rem" />}
                    title={
                        <div>
                            <div>
                                {item.name}
                                {!!item.hosts_assigned_count && (
                                    <>
                                        {" "}
                                        -{" "}
                                        <span className="color-primary">
                                            {plural(item.hosts_assigned_count, "host", "hosts")}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="font-size-small color-muted">{item.cpu_model}</div>
                        </div>
                    }
                    right={
                        <div className="font-size-small">
                            <div>
                                <span className="color-muted pr-2">CPU coef.:</span>
                                <span>{item.cpu_allocate_coefficient}</span>
                            </div>
                            <div>
                                <span className="color-muted pr-2">Mem coef.:</span>
                                <span>{item.mem_allocate_coefficient}</span>
                            </div>
                        </div>
                    }
                />
            )}
        </WSelect>
    );
}

export interface PoolSelectLoaderProps extends Omit<PoolSelectProps, "items"> {}

function PoolSelectLoader(props: PoolSelectLoaderProps) {
    const { basePoolUuid } = props;

    const data = useSuspenseQueryAtom(adminHostPoolQueryAtom(props.location));

    // Only host pools with the same storage pool can be selected
    const basePool = data.get(basePoolUuid);
    softInvariant(basePool != null, "Base pool not found: %s", basePoolUuid);

    const pools = [...data.values()].filter((item) => item.storage_pool_uuid === basePool?.storage_pool_uuid);
    softInvariant(pools.length > 0, "No pools found for base pool: %s", basePoolUuid);

    return <PoolSelectCustom items={pools} defaultValueKey={props.valueKey} {...props} />;
}

export function PoolSelect(props: PoolSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <PoolSelectCustom {...props} {...renderProps} />}>
            <PoolSelectLoader {...props} />
        </WSelectWrapper>
    );
}

// cspell:words coef
